import { useState, useEffect, useRef } from 'react';
import { NoteKey } from '../hooks';

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  avatar?: string;
  isHusky?: boolean;
}

interface TeamMembersProps {
  title: string;
  subtitle: string;
  members: TeamMember[];
  isVisible: boolean;
  onPlayNote: (note: NoteKey) => void;
}

// Minecraft-style 3D avatar component
function MinecraftAvatar({
  member,
  index,
  rotation,
}: {
  member: TeamMember;
  index: number;
  rotation: number;
}) {
  const skinColors = ['#e0ac69', '#f1c27d', '#ffdbac', '#c68642'];
  const hairColors = ['#1a1a2e', '#3d2314', '#8b4513', '#2c1810'];
  const shirtColors = ['#00d4ff', '#ff9500', '#4ade80', '#a855f7'];

  const skinColor = skinColors[index % skinColors.length];
  const hairColor = hairColors[index % hairColors.length];
  const shirtColor = shirtColors[index % shirtColors.length];

  return (
    <div
      className="relative w-32 h-40 mx-auto"
      style={{
        perspective: '500px',
        transformStyle: 'preserve-3d',
      }}
    >
      <div
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transform: `rotateY(${rotation}deg)`,
          transition: 'none',
        }}
      >
        {/* Head */}
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 w-16 h-16"
          style={{ transformStyle: 'preserve-3d' }}
        >
          <div
            className="absolute inset-0 rounded-sm overflow-hidden border-2 border-black/20"
            style={{
              transform: 'translateZ(8px)',
              backgroundColor: skinColor,
            }}
          >
            {member.avatar ? (
              <img
                src={member.avatar}
                alt={member.name}
                className="w-full h-full object-cover"
                style={{ imageRendering: 'pixelated' }}
              />
            ) : (
              <svg viewBox="0 0 8 8" className="w-full h-full" style={{ imageRendering: 'pixelated' }}>
                <rect x="1" y="2" width="2" height="2" fill="#1a1a2e" />
                <rect x="5" y="2" width="2" height="2" fill="#1a1a2e" />
                <rect x="1" y="2" width="1" height="1" fill="#fff" opacity="0.5" />
                <rect x="5" y="2" width="1" height="1" fill="#fff" opacity="0.5" />
                <rect x="3" y="4" width="2" height="1" fill={skinColor} opacity="0.7" />
                <rect x="2" y="6" width="4" height="1" fill="#8b4513" opacity="0.6" />
                <rect x="0" y="0" width="8" height="1" fill={hairColor} />
                <rect x="0" y="1" width="1" height="1" fill={hairColor} />
                <rect x="7" y="1" width="1" height="1" fill={hairColor} />
              </svg>
            )}
          </div>
          <div
            className="absolute inset-0 rounded-sm"
            style={{
              transform: 'translateZ(-8px) rotateY(180deg)',
              backgroundColor: hairColor,
            }}
          />
          <div
            className="absolute top-0 w-4 h-16 rounded-sm"
            style={{
              transform: 'translateX(-2px) rotateY(-90deg)',
              backgroundColor: skinColor,
              filter: 'brightness(0.8)',
            }}
          />
          <div
            className="absolute top-0 right-0 w-4 h-16 rounded-sm"
            style={{
              transform: 'translateX(2px) rotateY(90deg)',
              backgroundColor: skinColor,
              filter: 'brightness(0.9)',
            }}
          />
          <div
            className="absolute left-0 w-16 h-4 rounded-sm"
            style={{
              transform: 'translateY(-2px) rotateX(90deg)',
              backgroundColor: hairColor,
            }}
          />
        </div>

        {/* Body */}
        <div
          className="absolute left-1/2 -translate-x-1/2 top-16 w-16 h-20"
          style={{ transformStyle: 'preserve-3d' }}
        >
          <div
            className="absolute inset-0 rounded-sm border-2 border-black/20"
            style={{
              transform: 'translateZ(4px)',
              backgroundColor: shirtColor,
            }}
          >
            <div className="absolute inset-x-0 top-0 h-2 bg-white/20" />
            <div className="absolute left-1/2 -translate-x-1/2 top-4 w-1 h-8 bg-black/10" />
          </div>
          <div
            className="absolute top-0 left-0 w-2 h-20 rounded-sm"
            style={{
              transform: 'translateX(-1px) rotateY(-90deg)',
              backgroundColor: shirtColor,
              filter: 'brightness(0.7)',
            }}
          />
          <div
            className="absolute top-0 right-0 w-2 h-20 rounded-sm"
            style={{
              transform: 'translateX(1px) rotateY(90deg)',
              backgroundColor: shirtColor,
              filter: 'brightness(0.85)',
            }}
          />
        </div>

        {/* Arms */}
        <div
          className="absolute top-16 w-4 h-20"
          style={{
            left: 'calc(50% - 40px)',
            transformStyle: 'preserve-3d',
          }}
        >
          <div
            className="absolute inset-0 rounded-sm"
            style={{ backgroundColor: shirtColor, filter: 'brightness(0.8)' }}
          />
          <div
            className="absolute bottom-0 left-0 right-0 h-4 rounded-sm"
            style={{ backgroundColor: skinColor }}
          />
        </div>
        <div
          className="absolute top-16 w-4 h-20"
          style={{
            left: 'calc(50% + 36px)',
            transformStyle: 'preserve-3d',
          }}
        >
          <div
            className="absolute inset-0 rounded-sm"
            style={{ backgroundColor: shirtColor, filter: 'brightness(0.9)' }}
          />
          <div
            className="absolute bottom-0 left-0 right-0 h-4 rounded-sm"
            style={{ backgroundColor: skinColor }}
          />
        </div>

        {/* Shadow */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-4 rounded-full bg-black/20 blur-sm"
          style={{ transform: 'translateY(8px) rotateX(90deg)' }}
        />
      </div>
    </div>
  );
}

// Fluxky - Improved Minecraft Husky (sitting pose)
function MinecraftHusky({ rotation }: { rotation: number }) {
  return (
    <div
      className="relative w-40 h-48 mx-auto"
      style={{
        perspective: '800px',
        transformStyle: 'preserve-3d',
      }}
    >
      <style>{`
        @keyframes tailWag {
          0%, 100% { transform: rotateZ(-15deg) rotateY(10deg); }
          50% { transform: rotateZ(-10deg) rotateY(40deg); }
        }
        .animate-tail-wag {
          animation: tailWag 0.8s ease-in-out infinite;
          transform-origin: left center;
        }
      `}</style>

      <div
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transform: `rotateY(${rotation}deg) translateY(40px)`,
        }}
      >
        {/* HEAD */}
        <div
          className="absolute"
          style={{
            left: 'calc(50% - 24px)',
            top: '-10px',
            width: '48px',
            height: '42px',
            transformStyle: 'preserve-3d',
            zIndex: 10,
          }}
        >
          {/* Face Front */}
          <div
            className="absolute inset-0 rounded-sm bg-[#f3f4f6] border border-black/20"
            style={{ transform: 'translateZ(15px)' }}
          >
            {/* Husky Forehead Mask */}
            <div
              className="absolute inset-0 bg-[#4b5563]"
              style={{
                clipPath: 'polygon(0 0, 100% 0, 100% 35%, 70% 35%, 50% 60%, 30% 35%, 0 35%)',
              }}
            />
            {/* Eyes */}
            <div className="absolute left-[10px] top-[18px] w-2 h-2 bg-[#0ea5e9] rounded-sm border border-white" />
            <div className="absolute right-[10px] top-[18px] w-2 h-2 bg-[#0ea5e9] rounded-sm border border-white" />

            {/* Snout - 3D */}
            <div
              className="absolute left-1/2 -translate-x-1/2 top-[24px] w-6 h-4"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Snout Front */}
              <div
                className="absolute inset-0 bg-white border border-gray-200 rounded-sm"
                style={{ transform: 'translateZ(6px)' }}
              >
                <div className="absolute left-1/2 -translate-x-1/2 top-1 w-2.5 h-1.5 bg-[#1f2937] rounded-full" />
              </div>
              {/* Snout Back */}
              <div
                className="absolute inset-0 bg-gray-100 rounded-sm"
                style={{ transform: 'translateZ(-2px)' }}
              />
              {/* Snout Left */}
              <div
                className="absolute top-0 h-4 bg-gray-50 rounded-sm"
                style={{
                  transform: 'translateX(-1px) rotateY(-90deg)',
                  width: '8px',
                }}
              />
              {/* Snout Right */}
              <div
                className="absolute top-0 right-0 h-4 bg-gray-50 rounded-sm"
                style={{
                  transform: 'translateX(1px) rotateY(90deg)',
                  width: '8px',
                }}
              />
              {/* Snout Top */}
              <div
                className="absolute left-0 w-6 bg-white rounded-sm"
                style={{
                  transform: 'translateY(-1px) rotateX(90deg)',
                  height: '8px',
                }}
              />
              {/* Snout Bottom */}
              <div
                className="absolute left-0 bottom-0 w-6 bg-gray-100 rounded-sm"
                style={{
                  transform: 'translateY(1px) rotateX(-90deg)',
                  height: '8px',
                }}
              />
            </div>
          </div>

          {/* Face Back */}
          <div
            className="absolute inset-0 rounded-sm"
            style={{
              transform: 'translateZ(-15px) rotateY(180deg)',
              background: '#6b7280',
            }}
          />

          {/* Face Left Side */}
          <div
            className="absolute top-0 w-8 h-42 rounded-sm"
            style={{
              transform: 'translateX(-4px) rotateY(-90deg)',
              backgroundColor: '#9ca3af',
              filter: 'brightness(0.8)',
              width: '30px',
              height: '42px',
            }}
          />

          {/* Face Right Side */}
          <div
            className="absolute top-0 right-0 w-8 h-42 rounded-sm"
            style={{
              transform: 'translateX(4px) rotateY(90deg)',
              backgroundColor: '#9ca3af',
              filter: 'brightness(0.9)',
              width: '30px',
              height: '42px',
            }}
          />

          {/* Face Top */}
          <div
            className="absolute left-0 w-48 h-8 rounded-sm"
            style={{
              transform: 'translateY(-4px) rotateX(90deg)',
              backgroundColor: '#4b5563',
              width: '48px',
              height: '30px',
            }}
          />

          {/* Left Ear - 3D */}
          <div
            className="absolute -left-1 -top-6 w-4 h-8"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Ear Front */}
            <div
              className="absolute inset-0 bg-[#4b5563]"
              style={{
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                transform: 'translateZ(3px)',
              }}
            />
            {/* Ear Back */}
            <div
              className="absolute inset-0 bg-[#374151]"
              style={{
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                transform: 'translateZ(-3px) rotateY(180deg)',
              }}
            />
            {/* Ear Left Side */}
            <div
              className="absolute left-0 top-0 h-8 bg-[#4b5563]"
              style={{
                width: '6px',
                transform: 'translateX(-1.5px) rotateY(-90deg)',
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                filter: 'brightness(0.7)',
              }}
            />
            {/* Ear Right Side */}
            <div
              className="absolute right-0 top-0 h-8 bg-[#4b5563]"
              style={{
                width: '6px',
                transform: 'translateX(1.5px) rotateY(90deg)',
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                filter: 'brightness(0.8)',
              }}
            />
          </div>

          {/* Right Ear - 3D */}
          <div
            className="absolute -right-1 -top-6 w-4 h-8"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Ear Front */}
            <div
              className="absolute inset-0 bg-[#4b5563]"
              style={{
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                transform: 'translateZ(3px)',
              }}
            />
            {/* Ear Back */}
            <div
              className="absolute inset-0 bg-[#374151]"
              style={{
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                transform: 'translateZ(-3px) rotateY(180deg)',
              }}
            />
            {/* Ear Left Side */}
            <div
              className="absolute left-0 top-0 h-8 bg-[#4b5563]"
              style={{
                width: '6px',
                transform: 'translateX(-1.5px) rotateY(-90deg)',
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                filter: 'brightness(0.7)',
              }}
            />
            {/* Ear Right Side */}
            <div
              className="absolute right-0 top-0 h-8 bg-[#4b5563]"
              style={{
                width: '6px',
                transform: 'translateX(1.5px) rotateY(90deg)',
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                filter: 'brightness(0.8)',
              }}
            />
          </div>
        </div>

        {/* BODY (Sitting Posture) */}
        <div
          className="absolute left-1/2 -translate-x-1/2 top-[32px] w-14 h-20"
          style={{ transformStyle: 'preserve-3d' }}
        >
          {/* Body Front */}
          <div
            className="absolute inset-0 rounded-sm border border-black/20"
            style={{
              transform: 'translateZ(8px)',
              background: 'linear-gradient(180deg, #9ca3af 0%, #e5e7eb 100%)',
            }}
          >
            {/* White Chest Fur */}
            <div className="absolute inset-x-2 top-2 bottom-4 bg-white/90 rounded-full blur-[1px]" />
          </div>

          {/* Body Back */}
          <div
            className="absolute inset-0 rounded-sm"
            style={{
              transform: 'translateZ(-8px) rotateY(180deg)',
              background: '#6b7280',
            }}
          />

          {/* Body Left Side */}
          <div
            className="absolute top-0 w-4 h-20 rounded-sm"
            style={{
              transform: 'translateX(-2px) rotateY(-90deg)',
              backgroundColor: '#9ca3af',
              filter: 'brightness(0.7)',
              width: '16px',
            }}
          />

          {/* Body Right Side */}
          <div
            className="absolute top-0 right-0 w-4 h-20 rounded-sm"
            style={{
              transform: 'translateX(2px) rotateY(90deg)',
              backgroundColor: '#9ca3af',
              filter: 'brightness(0.85)',
              width: '16px',
            }}
          />
        </div>

        {/* FRONT PAWS (Vertical) */}
        <div className="absolute left-1/2 -translate-x-[20px] top-[75px] w-4 h-12 bg-white rounded-t-sm shadow-sm border-r border-gray-200" />
        <div className="absolute left-1/2 translate-x-[4px] top-[75px] w-4 h-12 bg-white rounded-t-sm shadow-sm border-l border-gray-200" />

        {/* BACK HIND LEGS (Tucked for sitting) */}
        <div className="absolute left-1/2 -translate-x-[34px] top-[85px] w-8 h-6 bg-[#9ca3af] rounded-full" />
        <div className="absolute left-1/2 translate-x-[14px] top-[85px] w-8 h-6 bg-[#9ca3af] rounded-full" />

        {/* TAIL (Positioned on the side, waving) */}
        <div
          className="absolute animate-tail-wag"
          style={{
            left: 'calc(50% + 18px)',
            top: '70px',
            width: '32px',
            height: '10px',
            background: 'linear-gradient(90deg, #6b7280 0%, #f9fafb 100%)',
            borderRadius: '20px',
            zIndex: -1,
          }}
        />

        {/* SHADOW */}
        <div
          className="absolute w-28 h-4 rounded-full bg-black/10 blur-md"
          style={{ left: '50%', top: '95px', transform: 'translateX(-50%)' }}
        />
      </div>
    </div>
  );
}

// Animated wrapper for avatar
function MinecraftAvatarAnimated({
  member,
  index,
  isSpinning,
  isHovered,
}: {
  member: TeamMember;
  index: number;
  isSpinning: boolean;
  isHovered: boolean;
}) {
  const [rotation, setRotation] = useState(0);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    if (!isSpinning) {
      cancelAnimationFrame(animationRef.current);
      return;
    }

    let lastTime = performance.now();
    const speed = 60 + index * 10;

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;
      setRotation((prev) => (prev + speed * deltaTime) % 360);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [isSpinning, index]);

  const finalRotation = isSpinning ? rotation : isHovered ? 15 : 0;

  return <MinecraftAvatar member={member} index={index} rotation={finalRotation} />;
}

// Animated wrapper for husky
function MinecraftHuskyAnimated({
  index,
  isSpinning,
  isHovered,
}: {
  index: number;
  isSpinning: boolean;
  isHovered: boolean;
}) {
  const [rotation, setRotation] = useState(0);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    if (!isSpinning) {
      cancelAnimationFrame(animationRef.current);
      return;
    }

    let lastTime = performance.now();
    const speed = 60 + index * 10;

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;
      setRotation((prev) => (prev + speed * deltaTime) % 360);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [isSpinning, index]);

  const finalRotation = isSpinning ? rotation : isHovered ? 15 : 0;

  return <MinecraftHusky rotation={finalRotation} />;
}

// Add husky as a team member
const huskyMember: TeamMember = {
  name: 'Fluxky',
  role: 'Office Mascot',
  bio: 'Chief morale officer. Specializes in debugging by barking at monitors and providing emotional support during late-night coding sessions.',
  isHusky: true,
};

export default function TeamMembers({
  title,
  subtitle,
  members,
  isVisible,
  onPlayNote,
}: TeamMembersProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [spinningCards, setSpinningCards] = useState<Set<number>>(() => new Set());

  const allMembers = [...members, huskyMember];

  useEffect(() => {
    setSpinningCards(new Set(allMembers.map((_, i) => i)));
  }, [allMembers.length]);

  const handleCardClick = (index: number) => {
    setSpinningCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
        onPlayNote('E5');
      } else {
        newSet.add(index);
        onPlayNote('C5');
      }
      return newSet;
    });
  };

  return (
    <section className="py-24 px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy-mid)] to-[var(--navy-deep)]" />

      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0, 212, 255, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 212, 255, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '16px 16px',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">{subtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {allMembers.map((member, i) => (
            <div
              key={i}
              className={`group relative text-center transition-all duration-500 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
              onClick={() => handleCardClick(i)}
              onMouseEnter={() => {
                setHoveredIndex(i);
                if (!spinningCards.has(i)) {
                  onPlayNote(['C5', 'E5', 'G5', 'A5', 'D5'][i % 5] as NoteKey);
                }
              }}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="mb-6 h-40 flex items-end justify-center transform group-hover:scale-105 transition-transform duration-300">
                {member.isHusky ? (
                  <MinecraftHuskyAnimated
                    index={i}
                    isSpinning={spinningCards.has(i)}
                    isHovered={hoveredIndex === i}
                  />
                ) : (
                  <MinecraftAvatarAnimated
                    member={member}
                    index={i}
                    isSpinning={spinningCards.has(i)}
                    isHovered={hoveredIndex === i}
                  />
                )}
              </div>

              {spinningCards.has(i) && (
                <div className="absolute top-2 right-2 text-xs text-[var(--cyan-electric)] opacity-60 animate-pulse">
                  Click to stop
                </div>
              )}

              <div className="relative inline-block">
                <div
                  className={`px-4 py-2 bg-black/60 rounded border-2 backdrop-blur-sm transition-colors ${
                    spinningCards.has(i) ? 'border-[var(--cyan-electric)]/40' : 'border-white/20'
                  }`}
                >
                  <h3 className="text-lg font-bold text-white group-hover:text-[var(--cyan-electric)] transition-colors">
                    {member.name}
                  </h3>
                </div>
              </div>

              <p
                className={`text-sm font-medium mt-2 ${
                  member.isHusky ? 'text-[var(--cyan-electric)]' : 'text-[var(--amber-warm)]'
                }`}
              >
                {member.role}
              </p>

              <div
                className={`mt-3 overflow-hidden transition-all duration-300 ${
                  hoveredIndex === i ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="text-gray-400 text-sm px-2">{member.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
